import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { QuestionnaireState, QuestionnaireFormData } from '../types/questionnaire';

interface QuestionnaireStore extends QuestionnaireState {
  updateFormData: (data: Partial<QuestionnaireFormData>) => void;
  nextStep: () => void;
  prevStep: () => void;
  setStep: (step: number) => void;
  validateCurrentStep: () => boolean;
  resetQuestionnaire: () => void;
  completeQuestionnaire: () => void;
}

const initialSteps = [
  {
    id: 'website-url',
    title: 'Website URL',
    description: 'Tell us about your website to personalize your experience',
    isCompleted: false,
    isActive: true,
  },
  {
    id: 'business-category',
    title: 'What is your business?',
    description: 'Choose a category that best describes your business',
    isCompleted: false,
    isActive: false,
  },
  {
    id: 'annual-revenue',
    title: "What's your annual revenue?",
    description: 'This helps us understand your business size and needs',
    isCompleted: false,
    isActive: false,
  },
  {
    id: 'team-size',
    title: "What's your team size?",
    description: 'Help us tailor our recommendations for your team',
    isCompleted: false,
    isActive: false,
  },
  {
    id: 'referral-source',
    title: 'How did you hear about us?',
    description: "We'd love to know how you discovered our platform",
    isCompleted: false,
    isActive: false,
  },
];

export const useQuestionnaireStore = create<QuestionnaireStore>()(
  persist(
    (set, get) => ({
      currentStep: 1,
      totalSteps: 5,
      formData: {
        websiteUrl: '',
        businessCategory: '',
        annualRevenue: '',
        teamSize: '',
        referralSource: '',
        step: 1,
      },
      steps: initialSteps,
      isValid: false,

      updateFormData: data =>
        set(state => ({
          formData: { ...state.formData, ...data },
        })),

      validateCurrentStep: () => {
        const { currentStep, formData } = get();
        switch (currentStep) {
          case 1:
            return (
              formData.websiteUrl.trim() !== '' &&
              formData.websiteUrl.includes('.') &&
              formData.websiteUrl.length > 3
            );
          case 2:
            return formData.businessCategory !== '';
          case 3:
            return formData.annualRevenue !== '';
          case 4:
            return formData.teamSize !== '';
          case 5:
            return formData.referralSource !== '';
          default:
            return true;
        }
      },

      nextStep: () =>
        set(state => {
          if (state.currentStep < state.totalSteps && state.validateCurrentStep()) {
            const newStep = state.currentStep + 1;
            const updatedSteps = state.steps.map((step, index) => ({
              ...step,
              isCompleted: index < newStep - 1,
              isActive: index === newStep - 1,
            }));

            return {
              currentStep: newStep,
              steps: updatedSteps,
              formData: { ...state.formData, step: newStep },
            };
          }
          return state;
        }),

      prevStep: () =>
        set(state => {
          if (state.currentStep > 1) {
            const newStep = state.currentStep - 1;
            const updatedSteps = state.steps.map((step, index) => ({
              ...step,
              isCompleted: index < newStep - 1,
              isActive: index === newStep - 1,
            }));

            return {
              currentStep: newStep,
              steps: updatedSteps,
              formData: { ...state.formData, step: newStep },
            };
          }
          return state;
        }),

      setStep: step =>
        set(state => {
          if (step >= 1 && step <= state.totalSteps) {
            const updatedSteps = state.steps.map((s, index) => ({
              ...s,
              isCompleted: index < step - 1,
              isActive: index === step - 1,
            }));

            return {
              currentStep: step,
              steps: updatedSteps,
              formData: { ...state.formData, step },
            };
          }
          return state;
        }),

      resetQuestionnaire: () =>
        set(() => ({
          currentStep: 1,
          formData: {
            websiteUrl: '',
            businessCategory: '',
            annualRevenue: '',
            teamSize: '',
            referralSource: '',
            step: 1,
          },
          steps: initialSteps,
          isValid: false,
        })),

      completeQuestionnaire: () => {
        console.log('Questionnaire completed');
      },
    }),
    {
      name: 'questionnaire-store',
      partialize: state => ({
        currentStep: state.currentStep,
        formData: state.formData,
        steps: state.steps,
      }),
    }
  )
);
